import { useQuery } from '@tanstack/react-query'
import { 
  Users, 
  Award, 
  TrendingUp, 
  Clock,
  UserPlus,
  FileText,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiGet } from '../lib/api'
import type { ComplianceData } from '../../../shared/types'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  color: 'blue' | 'green' | 'purple' | 'orange'
  subtitle?: string
}

function StatCard({ title, value, icon, color, subtitle }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-amber-50 text-amber-600',
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

interface QuickActionProps {
  title: string
  description: string
  icon: React.ReactNode
  href: string
  color: string
}

function QuickAction({ title, description, icon, href, color }: QuickActionProps) {
  return (
    <Link 
      to={href}
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all"
    >
      <div className={`p-3 rounded-lg ${color}`}>
        {icon}
      </div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </Link>
  )
}

export default function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'compliance'],
    queryFn: () => apiGet<{ compliance: ComplianceData }>('/admin/compliance'),
  })

  const compliance = data?.compliance

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your certification platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={compliance?.totalAllUsers || 0}
          icon={<Users className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Active Users"
          value={compliance?.activeUsersWithCerts || 0}
          icon={<TrendingUp className="w-6 h-6" />}
          color="green"
          subtitle="with certifications"
        />
        <StatCard
          title="Total Certifications"
          value={compliance?.totalCertifications || 0}
          icon={<Award className="w-6 h-6" />}
          color="purple"
        />
        <StatCard
          title="Avg per User"
          value={compliance?.avgCertsPerActiveUser || '0'}
          icon={<Clock className="w-6 h-6" />}
          color="orange"
          subtitle="certifications"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">New Users</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newUsers.today || 0}</p>
              <p className="text-sm text-gray-500">Today</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newUsers.thisWeek || 0}</p>
              <p className="text-sm text-gray-500">This Week</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newUsers.thisMonth || 0}</p>
              <p className="text-sm text-gray-500">This Month</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newUsers.thisYear || 0}</p>
              <p className="text-sm text-gray-500">This Year</p>
            </div>
          </div>
        </div>

        {/* New Certifications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">New Certifications</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newCertifications.today || 0}</p>
              <p className="text-sm text-gray-500">Today</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newCertifications.thisWeek || 0}</p>
              <p className="text-sm text-gray-500">This Week</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newCertifications.thisMonth || 0}</p>
              <p className="text-sm text-gray-500">This Month</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{compliance?.newCertifications.thisYear || 0}</p>
              <p className="text-sm text-gray-500">This Year</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users by Role */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h2>
        <div className="flex gap-8">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-blue-500 rounded-full" />
            <span className="text-gray-600">Users:</span>
            <span className="font-semibold text-gray-900">{compliance?.usersByRole.user || 0}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-amber-500 rounded-full" />
            <span className="text-gray-600">Admins:</span>
            <span className="font-semibold text-gray-900">{compliance?.usersByRole.admin || 0}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-red-500 rounded-full" />
            <span className="text-gray-600">Super Admins:</span>
            <span className="font-semibold text-gray-900">{compliance?.usersByRole.super_admin || 0}</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            title="Manage Users"
            description="View and manage users"
            icon={<UserPlus className="w-5 h-5 text-white" />}
            href="/admin/users"
            color="bg-blue-500"
          />
          <QuickAction
            title="View Reports"
            description="Compliance analytics"
            icon={<FileText className="w-5 h-5 text-white" />}
            href="/admin/reports"
            color="bg-green-500"
          />
          <QuickAction
            title="Send Email"
            description="Contact users"
            icon={<AlertTriangle className="w-5 h-5 text-white" />}
            href="/admin/email"
            color="bg-amber-500"
          />
          <QuickAction
            title="CE Management"
            description="Manage requirements"
            icon={<CheckCircle className="w-5 h-5 text-white" />}
            href="/admin/ce"
            color="bg-purple-500"
          />
        </div>
      </div>

      {/* Top Certifications */}
      {compliance?.topCertifications && compliance.topCertifications.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Certifications</h2>
          <div className="space-y-3">
            {compliance.topCertifications.slice(0, 5).map((cert, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-gray-700">{cert.name}</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">{cert.count} users</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
